/* Body measurements (state.measurements): the patch upsert both writers use,
 * per-site stats (latest, change over ~4 weeks, change since the first) and
 * the summary that relates waist to the 7-day average weight. Pure
 * functions; the Python twin is src/fitlogic/measurements.py and both are
 * pinned by vectors/measurements.json.
 *
 * measurements is [{ date, waist?, hips?, chest?, arm?, thigh?, neck?, note? }]
 * in cm, one entry per date, sorted, keyed by date (no id) so the sync merges
 * it per date and per field. An entry with no values is removed: a note
 * alone is not a measurement. Readers skip junk (no date, non-numbers). */

import SITE_DATA from './measurementSites.json';

const DATE_RE = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** [{ id, label, min, max, hint }] in display order (measurementSites.json). */
export const MEASUREMENT_SITES = SITE_DATA.sites;
export const SITE_IDS = MEASUREMENT_SITES.map(s => s.id);
/** Days after the last entry when the next tape check-in is due. */
export const DUE_DAYS = 14;
// "4 weeks" compares with the entry nearest 28 days back, 3-6 weeks back, so
// a fortnightly rhythm that drifts a day or two still gets a number.
const CHANGE_DAYS = 28;
const BAND_MIN = 21;
const BAND_MAX = 42;
const MIN_SPAN = 14;
const MIN_WEIGHT_CHANGE = 1; // kg; below it cm per kg is mostly noise

function isNum(v) {
  return typeof v === 'number' && Number.isFinite(v);
}

function isObj(v) {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

/* Half-up like the Python twin's floor(x * f + 0.5) / f; `|| 0` drops -0. */
function roundF(x, f) {
  return Math.round(x * f) / f || 0;
}
function round1(x) { return roundF(x, 10); }
function round2(x) { return roundF(x, 100); }

function dayNumber(key) {
  if (typeof key !== 'string' || !DATE_RE.test(key)) return null;
  const [y, m, d] = key.split('-').map(Number);
  const back = new Date(Date.UTC(y, m - 1, d));
  if (back.getUTCFullYear() !== y || back.getUTCMonth() !== m - 1 || back.getUTCDate() !== d) return null;
  return Math.round(back.getTime() / 86400000);
}

function keyOf(n) {
  return new Date(n * 86400000).toISOString().slice(0, 10);
}

function hasValues(entry) {
  return Object.keys(entry).some(k => k !== 'date' && k !== 'note' && isNum(entry[k]) && entry[k] > 0);
}

function byDate(a, b) {
  return a.date < b.date ? -1 : (a.date > b.date ? 1 : 0);
}

function signed(v) {
  return v > 0 ? `+${v}` : `${v}`;
}

function fmtDay(key) {
  const [, m, d] = key.split('-').map(Number);
  return `${d} ${MONTHS[m - 1]}`;
}

/**
 * Valid entries, sorted, one per date (a later duplicate's fields win), with
 * only the site values (finite, > 0) and a non-empty note; entries without a
 * site value are left out. Copies; the input is never changed.
 * @param {Array} measurements state.measurements (anything is tolerated)
 * @returns {Array<{date:string, waist?:number, hips?:number, chest?:number, arm?:number, thigh?:number, neck?:number, note?:string}>}
 */
export function cleanMeasurements(measurements) {
  const seen = new Map();
  for (const m of Array.isArray(measurements) ? measurements : []) {
    if (!isObj(m) || dayNumber(m.date) === null) continue;
    const entry = seen.get(m.date) || { date: m.date };
    for (const id of SITE_IDS) if (isNum(m[id]) && m[id] > 0) entry[id] = m[id];
    if (typeof m.note === 'string' && m.note.trim()) entry.note = m.note.trim();
    seen.set(m.date, entry);
  }
  return [...seen.values()].filter(hasValues).sort(byDate);
}

/**
 * Patch one date's entry. A site key in the patch with a number > 0 sets it,
 * any other value (null, 0, '') removes it; note: a non-empty string sets it
 * (trimmed), anything else removes it. Keys not in the patch, other fields
 * of the entry (e.g. a later armL) and other dates stay. Duplicates of the
 * date fold into one entry; items without a date string are dropped (they
 * would stop the sync from merging the array per date). An entry left with
 * no values is removed.
 * @param {Array} measurements state.measurements
 * @param {string} date YYYY-MM-DD; an invalid date changes nothing
 * @param {object} patch e.g. { waist: 84.5, neck: null }
 * @returns {Array} a new array sorted by date
 */
export function upsertMeasurement(measurements, date, patch) {
  const list = (Array.isArray(measurements) ? measurements : [])
    .filter(m => isObj(m) && typeof m.date === 'string');
  if (dayNumber(date) === null) return list.map(m => ({ ...m }));
  const others = list.filter(m => m.date !== date).map(m => ({ ...m }));
  const entry = Object.assign({}, ...list.filter(m => m.date === date), { date });
  const p = isObj(patch) ? patch : {};
  for (const id of SITE_IDS) {
    if (!Object.prototype.hasOwnProperty.call(p, id)) continue;
    if (isNum(p[id]) && p[id] > 0) entry[id] = p[id];
    else delete entry[id];
  }
  if (Object.prototype.hasOwnProperty.call(p, 'note')) {
    if (typeof p.note === 'string' && p.note.trim()) entry.note = p.note.trim();
    else delete entry.note;
  }
  const out = hasValues(entry) ? [...others, entry] : others;
  return out.sort(byDate);
}

/**
 * 7-day average weight on a date: the mean of every weigh-in from date-6 to
 * date (like the Stats chart's avg), 0.01 kg.
 * @returns {number|null} null without weigh-ins in those 7 days
 */
export function avgWeightOn(bodyMetrics, date) {
  const n = dayNumber(date);
  if (n === null) return null;
  const lo = keyOf(n - 6);
  let total = 0;
  let count = 0;
  for (const m of Array.isArray(bodyMetrics) ? bodyMetrics : []) {
    if (!isObj(m) || dayNumber(m.date) === null || !isNum(m.weight) || m.weight <= 0) continue;
    if (m.date < lo || m.date > date) continue;
    total += m.weight;
    count += 1;
  }
  return count ? round2(total / count) : null;
}

/**
 * One site over all entries.
 * @returns {{latest:number, date:string, count:number, firstDate:string, sinceFirst:number|null,
 *   change4w:number|null, change4wFrom:string|null}|null} null without a value. change4w
 *   compares with the entry nearest 28 days before the latest within 21-42 days (ties: the
 *   older one); sinceFirst needs 2 entries. Changes are 0.1 cm.
 */
export function siteStats(measurements, site) {
  const rows = cleanMeasurements(measurements).filter(m => isNum(m[site]));
  if (!rows.length) return null;
  const last = rows[rows.length - 1];
  const first = rows[0];
  const lastN = dayNumber(last.date);
  let base = null;
  let baseGap = null;
  for (const m of rows) {
    const gap = lastN - dayNumber(m.date);
    if (gap < BAND_MIN || gap > BAND_MAX) continue;
    const off = Math.abs(gap - CHANGE_DAYS);
    if (base === null || off < baseGap || (off === baseGap && dayNumber(m.date) < dayNumber(base.date))) {
      base = m;
      baseGap = off;
    }
  }
  return {
    latest: last[site],
    date: last.date,
    count: rows.length,
    firstDate: first.date,
    sinceFirst: rows.length >= 2 ? round1(last[site] - first[site]) : null,
    change4w: base ? round1(last[site] - base[site]) : null,
    change4wFrom: base ? base.date : null,
  };
}

/**
 * The tape check-in at a glance.
 * @param {object} state reads measurements, bodyMetrics
 * @param {string} today YYYY-MM-DD
 * @returns {{count:number, firstDate:string|null, lastDate:string|null, daysSince:number|null,
 *   due:boolean, sites:object, waistVsWeight:object|null, sentence:string|null}}
 *   sites: { [site]: siteStats } for sites with a value, in MEASUREMENT_SITES order. due:
 *   the last entry is DUE_DAYS+ days old. waistVsWeight (2+ waist entries 14+ days apart):
 *   { from, to, waistChange, weightFrom, weightTo, weightChange, cmPerKg } with the 7-day
 *   average weight on the first and last waist dates (null without weigh-ins); cmPerKg =
 *   waist change / weight change, only for a weight change of 1 kg or more.
 *   sentence: 'Since 1 Oct: weight -2.1 kg (7d avg), waist -2.4 cm, so about 1.1 cm waist
 *   per kg lost.'
 */
export function measurementSummary(state, today) {
  const st = isObj(state) ? state : {};
  const rows = cleanMeasurements(st.measurements);
  const out = {
    count: rows.length,
    firstDate: rows.length ? rows[0].date : null,
    lastDate: rows.length ? rows[rows.length - 1].date : null,
    daysSince: null,
    due: false,
    sites: {},
    waistVsWeight: null,
    sentence: null,
  };
  const t = dayNumber(today);
  if (rows.length && t !== null) {
    out.daysSince = Math.max(0, t - dayNumber(out.lastDate));
    out.due = out.daysSince >= DUE_DAYS;
  }
  for (const id of SITE_IDS) {
    const s = siteStats(rows, id);
    if (s) out.sites[id] = s;
  }
  const waist = rows.filter(m => isNum(m.waist));
  if (waist.length >= 2) {
    const a = waist[0];
    const b = waist[waist.length - 1];
    if (dayNumber(b.date) - dayNumber(a.date) >= MIN_SPAN) {
      const weightFrom = avgWeightOn(st.bodyMetrics, a.date);
      const weightTo = avgWeightOn(st.bodyMetrics, b.date);
      const both = weightFrom !== null && weightTo !== null;
      const dw = both ? weightTo - weightFrom : null;
      out.waistVsWeight = {
        from: a.date,
        to: b.date,
        waistChange: round1(b.waist - a.waist),
        weightFrom,
        weightTo,
        weightChange: both ? round1(dw) : null,
        cmPerKg: both && Math.abs(dw) >= MIN_WEIGHT_CHANGE ? round1((b.waist - a.waist) / dw) : null,
      };
      const w = out.waistVsWeight;
      let s = `Since ${fmtDay(w.from)}: `;
      if (both) s += `weight ${signed(w.weightChange)} kg (7d avg), `;
      s += `waist ${signed(w.waistChange)} cm`;
      if (w.cmPerKg !== null && w.cmPerKg > 0) {
        s += `, so about ${w.cmPerKg} cm waist per kg ${w.weightChange < 0 ? 'lost' : 'gained'}`;
      }
      out.sentence = `${s}.`;
    }
  }
  return out;
}
